/**
 * LDT wrapper for SlideImageSection that handles the Specimen & Patient Information
 * dialog and upload with slideMetadata via onFileAccepted callback.
 */
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SlideImageMetadataDialog } from '@ldt/features/orders/components/slide_image_metadata_dialog';
import { getSlideSiteOptionsForSpecimenType } from '@ldt/features/orders/utils/slide_site_options';
import { SlideImageSection } from 'src/distribution_platforms/features/components/slide_image_section';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { useUpload } from 'src/hooks/use_upload';
import {
  LdtOrderDetailFieldsFragment,
  SlideMetadataInputType,
  SpecimenType,
} from 'src/types';
import { UploadedFileCategory } from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import { SpecimenTypeUtils, notifySuccess } from 'src/utils';

export interface LdtSlideImageSectionProps {
  order: LdtOrderDetailFieldsFragment;
}

/** Order id from a path like /{org}/orders/{orderId} or .../orders/{orderId}/edit. */
function getOrderIdFromPathname(pathname: string): string | undefined {
  const match = pathname.match(/\/orders\/([^/]+)/);
  return match?.[1];
}

export const LdtSlideImageSection: FC<LdtSlideImageSectionProps> = ({
  order,
}) => {
  const { uploadFile } = useUpload();
  const navigate = useNavigate();
  const { organization } = useParams();
  const workflowType = useWorkflowType();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isMetadataDialogOpen, setMetadataDialogOpen] = useState(false);

  const handleFileAccepted = useCallback((file: File) => {
    setSelectedFile(file);
    setMetadataDialogOpen(true);
  }, []);

  const handleMetadataDialogClose = useCallback(() => {
    setMetadataDialogOpen(false);
    setSelectedFile(null);
  }, []);

  const handleMetadataSubmit = useCallback(
    (metadata: SlideMetadataInputType) => {
      if (!selectedFile) return;

      const onComplete =
        workflowType === WorkflowType.DIRECT_UPLOAD_LAB
          ? ({ orderId }: { orderId: string }) => {
              const viewingOrderId = getOrderIdFromPathname(
                window.location.pathname,
              );
              if (viewingOrderId !== orderId) return;
              notifySuccess('Slide image uploaded.');
              navigate(`/${organization}/orders`);
            }
          : undefined;

      uploadFile(
        selectedFile,
        order.id!,
        order.crossOrgId!,
        UploadedFileCategory.SlideImage,
        undefined,
        metadata,
        onComplete ? { onComplete } : undefined,
      );
      setMetadataDialogOpen(false);
      setSelectedFile(null);
    },
    [
      order.id,
      order.crossOrgId,
      selectedFile,
      uploadFile,
      workflowType,
      navigate,
      organization,
    ],
  );

  const specimenTypeLabel =
    order.specimenType != null
      ? SpecimenTypeUtils.getSpecimenTypeName(
          order.specimenType as SpecimenType,
        )
      : '—';

  const slideSiteOptions = useMemo(() => {
    return getSlideSiteOptionsForSpecimenType(order.specimenType);
  }, [order.specimenType]);

  return (
    <>
      <SlideImageSection
        order={order}
        onFileAccepted={handleFileAccepted}
        // TODO(Troy): Show download button when the backend is ready.
        showDownloadButton={false}
      />
      {selectedFile && (
        <SlideImageMetadataDialog
          open={isMetadataDialogOpen}
          onClose={handleMetadataDialogClose}
          order={order}
          selectedFile={selectedFile}
          specimenTypeLabel={specimenTypeLabel}
          slideSiteOptions={slideSiteOptions}
          onSubmit={handleMetadataSubmit}
        />
      )}
    </>
  );
};
